<template>
  <Form class="form-window" @submit="submit">
    <div class="formcell">
      <div class="profile-photo-block">
        <div
          class="profile-photo-wrap pointer"
          :class="{ 'profile-photo-wrap_avatar': avatar }"
          @click="choose"
        >
          <img v-if="avatar" :src="avatar" alt="user avatar" class="profile-img" />
          <div v-else class="profile-icon">
            <UserIcon />
          </div>
        </div>

        <div v-if="avatar" class="photo-block-actions spacex-12">
          <Button size="small" color="primary" type="ghost" @click="choose">
            {{ $t('Upload new photo') }}
          </Button>
          <Button size="small" color="danger" type="borderless" @click="remove">
            {{ $t('Remove photo') }}
          </Button>
        </div>
        <Button v-else size="small" color="primary" type="ghost" @click="choose">
          {{ $t('Upload your photo') }}
        </Button>

        <input ref="file" type="file" style="display: none" accept="image/*" @change="addPhoto" />
      </div>
    </div>

    <AddProjectFormInput
      id="full-name-input"
      v-model="name"
      required
      :max-length="30"
      :label-text="$t('Full name')"
    />
    <AddProjectFormInput
      id="position-input"
      v-model="position"
      required
      :max-length="30"
      :label-text="$t('Position')"
    />
    <div class="two-columns-form-row">
      <AddProjectFormInput
        id="phone-input"
        v-model="phone_number"
        :required="true"
        :label-text="$t('Phone number')"
      />
      <Select
        :value="$i18n.locale"
        :label-text="$t('Language')"
        :items="languagesList"
        @input="changeLocale"
      />
    </div>
    <AddProjectFormInput
      id="email-input"
      v-model="email"
      :disabled="true"
      type="email"
      :required="true"
      :label-text="$t('Email address')"
    >
      <template #info>
        <span class="info-message">
          <ContactSupportLink />
          <span>{{ $t('if you need to change email') }}</span>
        </span>
      </template>
    </AddProjectFormInput>
    <div class="user-form-custom-footer">
      <Button color="primary" action-type="submit">
        <Spinner v-if="isLoading" size="20" />
        <span v-else>
          {{ $t('Save changes') }}
        </span>
      </Button>
      <SuccessAlert v-if="success" class="mx-4" />
      <ErrorAlert v-else-if="error" :error="error" class="mx-4" />
    </div>
  </Form>
</template>

<script>
  import AddProjectFormInput from '@/components/ui/FormInput';
  import factory from '@/utils/api/factory';
  import { mapActions, mapGetters } from 'vuex';
  import UserIcon from '@/assets/icons/user.svg';
  import Form from '@/components/common/Form';
  import parseError from '@/utils/formatting/parse-error';
  import Spinner from 'vue-simple-spinner';
  import Button from '@/components/ui/Button';
  import { SUPPORT_EMAIL } from '@/utils/constants/common';
  import ContactSupportLink from '@/components/common/ContactSupportLink';
  import Select from '@/components/ui/Select';
  import SuccessAlert from '@/components/common/SuccessAlert';
  import ErrorAlert from '@/components/common/ErrorAlert';
  import { changeLocale } from '@/i18n';

  export default {
    name: 'UserSettingsGeneralForm',
    components: {
      ErrorAlert,
      SuccessAlert,
      Select,
      ContactSupportLink,
      Button,
      Form,
      AddProjectFormInput,
      UserIcon,
      Spinner,
    },
    props: {
      user: {
        type: Object,
        default: () => ({
          id: Number,
          email: String,
          name: String,
        }),
      },
    },
    data() {
      return {
        name: this.user.name,
        email: this.user.email,
        avatar: this.user.files_urls.avatar_thumbnail,
        position: this.user.position,
        phone_number: this.user.phone_number,
        file: null,
        success: false,
        error: null,
        isLoading: false,
        initialLang: this.$i18n.locale,
        SUPPORT_EMAIL,
      };
    },
    computed: {
      ...mapGetters('global', ['languagesList']),
    },
    methods: {
      changeLocale,
      ...mapActions({
        updateUser: 'auth/load',
        fetchProperties: 'global/fetchProperties',
      }),
      validate() {
        this.success = false;
        this.error = null;
        return !!(this.name && this.email);
      },
      async submit() {
        if (!this.validate()) return;

        this.isLoading = true;
        try {
          let body;
          if (this.avatar) {
            body = new FormData();
            body.append('name', this.name);
            body.append('email', this.email);
            body.append('position', this.position);
            body.append('phone_number', this.phone_number);
            body.append('language', this.$i18n.locale);
            if (this.file) body.append('avatar', this.file);
          } else {
            body = {
              name: this.name,
              email: this.email,
              avatar: this.file,
              position: this.position,
              phone_number: this.phone_number,
              language: this.$i18n.locale,
            };
          }

          await factory('profile').patch(body);

          this.success = true;
          this.error = null;
          await this.updateUser();
          // Если язык отличается от того, что был при открытии формы, то перезапрашиваем пропертис
          if (this.initialLang !== this.$i18n.locale) {
            await this.fetchProperties();
            this.initialLang = this.$i18n.locale;
          }
        } catch (e) {
          this.error =
            parseError(e, { returnDetail: true }) || this.$t('Sorry, something went wrong');
        }
        this.isLoading = false;
      },
      choose() {
        this.$refs.file.value = '';
        this.$refs.file.click();
      },
      remove() {
        this.avatar = null;
        this.file = null;
      },
      addPhoto(e) {
        const files = e.target.files;
        if (files.length !== 1) return;

        this.file = files[0];
        let fileReader = new FileReader();
        fileReader.readAsDataURL(files[0]);
        fileReader.onloadend = () => {
          this.avatar = fileReader.result;
        };
      },
    },
  };
</script>

<style>
  .w-form-fail,
  .w-form-done {
    display: block;
  }
  .two-columns-form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-column-gap: 30px;
  }
  .photo-block-actions {
    display: -webkit-box;
    display: -webkit-flex;
    display: -ms-flexbox;
    display: flex;
  }
  .profile-img {
    -webkit-transition: opacity 200ms ease;
    transition: opacity 200ms ease;
  }
  .profile-img:hover {
    opacity: 0.8;
  }
  .profile-icon {
    width: 24px;
    height: 24px;
  }
  .profile-photo-block {
    display: -webkit-box;
    display: -webkit-flex;
    display: -ms-flexbox;
    display: flex;
    -webkit-box-align: center;
    -webkit-align-items: center;
    -ms-flex-align: center;
    align-items: center;
  }
  .profile-photo-wrap_avatar {
    border-style: none;
  }
  .profile-photo-wrap {
    display: -webkit-box;
    display: -webkit-flex;
    display: -ms-flexbox;
    display: flex;
    overflow: hidden;
    width: 40px;
    height: 40px;
    margin-right: 12px;
    -webkit-box-pack: center;
    -webkit-justify-content: center;
    -ms-flex-pack: center;
    justify-content: center;
    -webkit-box-align: center;
    -webkit-align-items: center;
    -ms-flex-align: center;
    align-items: center;
    border-style: solid;
    border-width: 1px;
    border-color: #4887f7;
    border-radius: 50%;
    -webkit-transition: all 200ms ease;
    transition: all 200ms ease;
  }

  .profile-photo-wrap:hover {
    background-color: rgba(72, 135, 247, 0.08);
  }
</style>
